import { Diagnosis } from 'src/app/models/fhir-models';

/**
 * ICD-10 code prefixes that map a patient to the CHF care call guide.
 * CHF group per SOC v9 (see vital-flag-chf.model.ts): heart failure (I50.x) and
 * hypertensive heart disease with heart failure (I11.x / I13.x).
 */
export const CHF_ICD_PREFIXES = ['I50', 'I11', 'I13'];

/**
 * ICD-10 code prefixes that map a patient to the COPD care call guide.
 * COPD spectrum: COPD (J44), emphysema (J43), chronic bronchitis simple/
 * mucopurulent (J41) and unspecified chronic bronchitis (J42).
 */
export const COPD_ICD_PREFIXES = ['J44', 'J43', 'J41', 'J42'];

/**
 * ICD-10 code prefixes that map a patient to the HTN care call guide.
 * Scoped to essential (primary) hypertension (I10) for now; the rest of the
 * hypertensive block (e.g. I12/I15) is intentionally deferred to a later phase.
 * CHF is checked first, so hypertensive heart disease WITH heart failure
 * (I11.x / I13.x) still routes to 'chf', not 'htn'.
 */
export const HTN_ICD_PREFIXES = ['I10'];

/**
 * ICD-10 code prefixes that map a patient to the Type 2 Diabetes care call guide.
 * Type 2 diabetes mellitus only (E11.x) — type 1 (E10) and other/secondary diabetes
 * (E08/E09/E13) do NOT route here and fall through to the General guide.
 */
export const T2DM_ICD_PREFIXES = ['E11'];

export type GuideType = 'chf' | 'copd' | 'htn' | 't2dm' | 'general';

/** Program flags on a diagnosis association, in the order they read on the guide form. */
const PROGRAM_LABELS: { key: 'ccm' | 'apcm' | 'rpm' | 'pcm' | 'bhi'; label: string }[] = [
  { key: 'ccm', label: 'CCM' },
  { key: 'apcm', label: 'APCM' },
  { key: 'rpm', label: 'RPM' },
  { key: 'pcm', label: 'PCM' },
  { key: 'bhi', label: 'BHI' },
];

/**
 * Diagnoses ordering used to pick the patient's MAIN diagnosis: lowest `index` first;
 * legacy rows without an index sort last (treated as MAX_SAFE_INTEGER, which also guards
 * against `undefined - number = NaN`); same-index rows tie-break by diagnosis name so the
 * order stays deterministic. Shared with the Diagnoses table so the check_circle-flagged
 * main row and the guide routed by it never disagree (see fhir-diagnoses.component.ts).
 */
export const compareDiagnosesByMain = (a: Diagnosis, b: Diagnosis): number => {
  const indexA = a.index ?? Number.MAX_SAFE_INTEGER;
  const indexB = b.index ?? Number.MAX_SAFE_INTEGER;
  return indexA === indexB ? (a.diagnosis ?? '').localeCompare(b.diagnosis ?? '') : indexA - indexB;
};

/**
 * The patient's main diagnosis: the one the nurse ordered first in the Diagnoses
 * table (index 0, flagged with the check_circle indicator). Uses the shared
 * `compareDiagnosesByMain` ordering so the pick matches the table exactly. This
 * matters because getPatientDiagnoses returns the diagnoses unsorted, so ties
 * (e.g. all-legacy rows with no index) would otherwise resolve by arbitrary
 * Firestore order.
 */
export const resolveMainDiagnosis = (diagnoses: Diagnosis[] | null | undefined): Diagnosis | null => {
  if (!diagnoses || diagnoses.length === 0) {
    return null;
  }
  return diagnoses.slice().sort(compareDiagnosesByMain)[0];
};

/**
 * Resolves which Care Call Guide applies to a patient from their MAIN FHIR diagnosis
 * (see resolveMainDiagnosis — secondary diagnoses do not route the guide), in
 * priority order CHF -> COPD -> HTN -> T2DM -> General:
 * - main diagnosis is CHF-coded (I50/I11/I13) -> 'chf'
 * - main diagnosis is COPD-coded (J44/J43/J41/J42) -> 'copd'
 * - main diagnosis is HTN-coded (I10) -> 'htn' (CHF is checked first, so hypertensive
 *   heart disease WITH heart failure still routes to 'chf')
 * - main diagnosis is T2DM-coded (E11) -> 't2dm' (type 1 E10 and E08/E09/E13 do not)
 * - main diagnosis is anything else -> 'general', even when a secondary diagnosis
 *   is CHF/COPD/HTN/T2DM (only the main diagnosis routes)
 * - no diagnosis on file -> 'general' (temporary: patients without a diagnosis
 *   still get the General guide for now; return `null` here to bring back the
 *   "no care call guide available" behavior)
 */
export const resolveGuideType = (diagnoses: Diagnosis[] | null | undefined): GuideType | null => {
  const main = resolveMainDiagnosis(diagnoses);
  if (!main) {
    return 'general';
  }
  const code = (main.code_value ?? '').toUpperCase();
  if (CHF_ICD_PREFIXES.some((prefix) => code.startsWith(prefix))) {
    return 'chf';
  }
  if (COPD_ICD_PREFIXES.some((prefix) => code.startsWith(prefix))) {
    return 'copd';
  }
  if (HTN_ICD_PREFIXES.some((prefix) => code.startsWith(prefix))) {
    return 'htn';
  }
  if (T2DM_ICD_PREFIXES.some((prefix) => code.startsWith(prefix))) {
    return 't2dm';
  }
  return 'general';
};

/**
 * Distinct program labels (CCM / APCM / RPM / PCM / BHI) across the patient's
 * diagnosis associations — shown as the "Program" line in the generated note header.
 */
export const patientProgramLabels = (diagnoses: Diagnosis[] | null | undefined): string[] => {
  const list = diagnoses ?? [];
  return PROGRAM_LABELS.filter(({ key }) => list.some((dx) => dx.association?.[key])).map(({ label }) => label);
};
